import React from 'react';
import * as Styled from './styled';
import { LeadingAgentCard } from './leading-agent-card';
import { LeadingAgentCarouselCardData } from 'utils/entities';
import { useImageUrlBuilder } from 'hooks/useImageUrlBuilder';
import { BaseCarousel } from 'ds/components/carousels/base-carousel';
import theme from 'ds/theme/default';
import CarouselBGIcon from 'assets/svg/city-front.svg';
import CarouselBGSoloIcon from 'assets/svg/city-front-solo.svg';
import LeadingCheckIcon from 'assets/svg/leading-check.svg';
import { useLocale } from 'locale';
import { useScreenBreakpoint, BreakpointValue } from 'consts/breakpoints';
import { noop } from 'lodash';
import { useIntersectionObserver } from 'hooks/useIntersectionObserver';

enum Direction {
  Left = 'left',
  Right = 'right',
}

enum Action {
  Click = 'click',
  Swipe = 'swipe',
}

export interface EventProps {
  direction: Direction;
  action: Action;
}

export interface AgentDetails {
  id: string;
  office_name: string;
  name: string;
  is_leading_agent: true;
}

function getDirection(current: number, next: number) {
  const isLeft = current < next;
  return isLeft ? Direction.Left : Direction.Right;
}

function getAction(isMobile: boolean) {
  return isMobile ? Action.Swipe : Action.Click;
}

export interface LeadingAgentsCarouselProps {
  source: 'search-page' | 'hood-page' | 'address-page' | 'city-page' | 'school-page';
  items: LeadingAgentCarouselCardData[];
  onCarouselScroll?: (d: EventProps) => void;
  onCardClick?: (d: AgentDetails) => void;
  onCardExposeEvent?: (d: AgentDetails) => void;
}

function checkShadowDisplayAndControlsByScreenAndSize(b: BreakpointValue, size: number) {
  const twoOrLessItems = size <= 2;
  const onlyOne = size === 1;

  switch (b) {
    case 5:
    case 4:
      return twoOrLessItems;
    default:
      return onlyOne;
  }
}

function getRightPadding(b: BreakpointValue, isAreaPage: boolean) {
  switch (b) {
    case 2:
      return isAreaPage ? 6 : 3;
    case 3:
    case 4:
      return isAreaPage ? 4 : 3;
    default:
      return 3;
  }
}

export const LeadingAgentsCarousel: React.FC<LeadingAgentsCarouselProps> = (props) => {
  const {
    source,
    items,
    onCarouselScroll = noop,
    onCardClick = noop,
    onCardExposeEvent,
  } = props;

  const [ sectionInView, setSectionInView ] = React.useState(false);
  const carouselSectionNode = React.useRef<HTMLDivElement>(null);

  useIntersectionObserver(([ entry ]) => {
    setSectionInView(entry.isIntersecting);
  }, [ carouselSectionNode ], { threshold: 0 });

  const constructImageURI = useImageUrlBuilder();
  const [ slideIndex, setSlideIndex ] = React.useState(0);
  const { t } = useLocale();
  const breakpoint = useScreenBreakpoint();

  const isCityPage = source === 'city-page';
  const isHoodPage = source === 'hood-page';
  const isSchoolPage = source === 'school-page';
  const isAddressPage = source === 'address-page';
  const isAreaPage = isAddressPage || isHoodPage || isCityPage || isSchoolPage;
  const isBigDesktop = breakpoint >= 4;
  const isMobile = breakpoint <= 2;
  const paddingRight = getRightPadding(breakpoint, isAreaPage);

  const onSlide = (idx: number) => {
    const direction = getDirection(slideIndex, idx);
    const action = getAction(isMobile);
    onCarouselScroll({ direction, action });
    setSlideIndex(idx);
  };

  const twoOrLessItems = items.length <= 2;
  const showCarouselBGIcon = twoOrLessItems && isBigDesktop && !isAreaPage;
  const hideShadowAndControls = checkShadowDisplayAndControlsByScreenAndSize(breakpoint, items.length) && !isAreaPage;

  return (
    <Styled.CarouselRoot withBorder={isAreaPage && !isCityPage} paddingRight={paddingRight} bgColor={isAreaPage ? theme.colors.neutrals.white : theme.colors.brand.secondary} disableMargin={isAreaPage} slideIndex={slideIndex}>
      {showCarouselBGIcon ? (
        <Styled.BGIconWrapper>
          {items.length === 1 ? <CarouselBGSoloIcon /> :  <CarouselBGIcon />}
        </Styled.BGIconWrapper>
      ) : null}
      <Styled.Header>
        <Styled.LeadingIconWrapper>
          <LeadingCheckIcon />
        </Styled.LeadingIconWrapper>
        <div>
          <Styled.Title darker={isAreaPage}>
            {t('leading.agent.search.carousel.title')}
          </Styled.Title>
          <Styled.SubTitle darker={isAreaPage} small={isMobile}>
            {t(isAreaPage ? 'leading.agent.search.hood.subtitle' : 'leading.agent.search.carousel.subtitle')}
          </Styled.SubTitle>
        </div>
      </Styled.Header>
      <Styled.CarouselWrapper ref={carouselSectionNode}>
        <BaseCarousel
          customGradientColor={isAreaPage ? theme.colors.neutrals.white : theme.colors.brand.secondary}
          entryContentDXOverride={24}
          hideShadow={hideShadowAndControls}
          hideControls={hideShadowAndControls}
          onSlideIndexChange={onSlide}
          activeCarouselSlide={slideIndex}
        >
          {items.map(item => (
            <LeadingAgentCard
              key={item.agentId}
              officeName={item.officeName}
              agentName={item.agentName}
              agentId={item.agentId}
              onCardExposeEvent={onCardExposeEvent}
              sectionInView={sectionInView}
              rootNode={carouselSectionNode}
              onClick={() => onCardClick({ id: item.agentId, name: item.agentName, office_name: item.officeName, is_leading_agent: true })}
              agentLogo={constructImageURI(item.agentLogo)}
            />
          ))}
        </BaseCarousel>
      </Styled.CarouselWrapper>
    </Styled.CarouselRoot>
  );
};
